import React, { useState } from "react";
// @ts-expect-error - this will be resolved at build time
import { Box, Text, Spinner } from "@xsolla/xui-primitives";
import { useResolvedTheme, type ThemeOverrideProps } from "@xsolla/xui-core";

interface AppButtonTokens {
  bg: string;
  bgHover: string;
  bgPress: string;
  border: string;
  borderHover: string;
  borderPress: string;
  text: string;
  textDisable: string;
}

interface ButtonSizeStyles {
  height: number;
  padding: number;
  fontSize: number;
  sublabelFontSize: number;
  spinnerSize: number;
  iconSize: number;
  iconContainerSize: number;
  borderRadius: number;
  labelIconSize: number;
  labelIconGap: number;
}

const cloneIconWithDefaults = (
  icon: React.ReactNode,
  defaultSize: number,
  defaultColor: string
): React.ReactNode => {
  if (!React.isValidElement(icon)) return icon;

  const iconElement = icon as React.ReactElement<any>;
  const existingProps = iconElement.props || {};

  return React.cloneElement(iconElement, {
    ...existingProps,
    size: existingProps.size ?? defaultSize,
    color: existingProps.color ?? defaultColor,
  });
};

export interface AppButtonProps extends ThemeOverrideProps {
  /** Size of the button */
  size?: "xl" | "lg" | "md" | "sm" | "xs";
  /** Whether the button is disabled */
  disabled?: boolean;
  /** Whether the button is in a loading state */
  loading?: boolean;
  /** Button content */
  children: React.ReactNode;
  /** Click handler */
  onPress?: () => void;
  /** Icon to display on the left side */
  iconLeft?: React.ReactNode;
  /** Icon to display on the right side */
  iconRight?: React.ReactNode;
  /** Secondary text displayed inline with the main label */
  sublabel?: string;
  /** Alignment of the label text */
  labelAlignment?: "left" | "center";
  /** Small icon displayed directly next to the label text */
  labelIcon?: React.ReactNode;
  /** Custom content slot for badges, tags, or other elements */
  customContent?: React.ReactNode;
  /** Accessible label for screen readers */
  "aria-label"?: string;
  /** ID of element that describes this button */
  "aria-describedby"?: string;
  /** Indicates the button controls an expandable element */
  "aria-expanded"?: boolean;
  /** Indicates the type of popup triggered by the button */
  "aria-haspopup"?: boolean | "menu" | "listbox" | "tree" | "grid" | "dialog";
  /** Indicates the button is pressed (for toggle buttons) */
  "aria-pressed"?: boolean | "mixed";
  /** ID of the element this button controls */
  "aria-controls"?: string;
  testID?: string;
  id?: string;
  /** HTML type attribute for the button */
  type?: "button" | "submit" | "reset";
  /** Whether the button should stretch to fill the full width of its container */
  fullWidth?: boolean;
}

/**
 * AppButton - A prominent filled button for app-level actions.
 *
 * Uses the `control.appButton` theme tokens for styling.
 * Supports all the same layout features as Button (icons, sublabels, etc.).
 */
export const AppButton: React.FC<AppButtonProps> = ({
  size = "md",
  disabled = false,
  loading = false,
  children,
  onPress,
  iconLeft,
  iconRight,
  sublabel,
  labelAlignment = "center",
  labelIcon,
  customContent,
  "aria-label": ariaLabel,
  "aria-describedby": ariaDescribedBy,
  "aria-expanded": ariaExpanded,
  "aria-haspopup": ariaHasPopup,
  "aria-pressed": ariaPressed,
  "aria-controls": ariaControls,
  testID,
  id,
  type = "button",
  fullWidth = false,
  themeMode,
  themeProductContext,
}) => {
  const { theme } = useResolvedTheme({ themeMode, themeProductContext });
  const [isKeyboardPressed, setIsKeyboardPressed] = useState(false);

  const isDisabled = disabled || loading;

  const sizeStyles = theme.sizing.button(size) as ButtonSizeStyles;

  const tokens: AppButtonTokens = (theme?.colors?.control as any)
    ?.appButton || {
    bg: "#34474b",
    bgHover: "#3d5256",
    bgPress: "#2b3b3e",
    border: "rgba(255, 255, 255, 0.12)",
    borderHover: "rgba(255, 255, 255, 0.18)",
    borderPress: "rgba(255, 255, 255, 0.12)",
    text: "#b7c5c8",
    textDisable: "#b3b3b3",
  };

  const handlePress = () => {
    if (!isDisabled && onPress) {
      onPress();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (isDisabled) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      setIsKeyboardPressed(true);
    }
  };

  const handleKeyUp = (e: React.KeyboardEvent) => {
    if (isDisabled) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      setIsKeyboardPressed(false);
      if (onPress) {
        onPress();
      }
    }
  };

  let backgroundColor = tokens.bg;
  if (disabled) {
    backgroundColor = tokens.bg;
  } else if (isKeyboardPressed) {
    backgroundColor = tokens.bgPress;
  }

  const borderColor = tokens.border;
  const textColor = disabled ? tokens.textDisable : tokens.text;

  return (
    <Box
      as="button"
      type={type}
      id={id}
      onPress={handlePress}
      onKeyDown={handleKeyDown}
      onKeyUp={handleKeyUp}
      disabled={isDisabled}
      aria-label={ariaLabel}
      aria-disabled={isDisabled || undefined}
      aria-busy={loading || undefined}
      aria-describedby={ariaDescribedBy}
      aria-expanded={ariaExpanded}
      aria-haspopup={ariaHasPopup}
      aria-pressed={ariaPressed}
      aria-controls={ariaControls}
      testID={testID}
      backgroundColor={backgroundColor}
      borderColor={borderColor}
      borderWidth={
        borderColor !== "transparent" &&
        borderColor !== "rgba(255, 255, 255, 0)" &&
        borderColor !== "rgba(0, 0, 0, 0)" &&
        !borderColor.endsWith(", 0)")
          ? 1
          : 0
      }
      borderRadius={sizeStyles.borderRadius}
      height={sizeStyles.height}
      width={fullWidth ? "100%" : undefined}
      padding={0}
      flexDirection="row"
      alignItems="center"
      justifyContent="center"
      position="relative"
      cursor={disabled ? "not-allowed" : loading ? "wait" : "pointer"}
      opacity={disabled ? 0.6 : 1}
      hoverStyle={
        !isDisabled
          ? {
              backgroundColor: tokens.bgHover,
              borderColor: tokens.borderHover,
            }
          : undefined
      }
      pressStyle={
        !isDisabled
          ? {
              backgroundColor: tokens.bgPress,
              borderColor: tokens.borderPress,
            }
          : undefined
      }
      focusStyle={{
        outlineColor: theme.colors.border.brand,
        outlineWidth: 2,
        outlineOffset: 2,
        outlineStyle: "solid",
      }}
    >
      {loading && (
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          alignItems="center"
          justifyContent="center"
          zIndex={1}
        >
          <Spinner
            color={textColor}
            size={sizeStyles.spinnerSize}
            aria-hidden={true}
          />
        </Box>
      )}

      {iconLeft && (
        <Box
          width={sizeStyles.iconContainerSize}
          height={sizeStyles.iconContainerSize}
          alignItems="center"
          justifyContent="center"
          aria-hidden={true}
          style={{
            opacity: loading ? 0 : 1,
            pointerEvents: loading ? "none" : "auto",
          }}
        >
          {cloneIconWithDefaults(iconLeft, sizeStyles.iconSize, textColor)}
        </Box>
      )}

      <Box
        flex={fullWidth ? 1 : undefined}
        flexDirection="row"
        alignItems="center"
        justifyContent={labelAlignment === "left" ? "flex-start" : "center"}
        paddingHorizontal={sizeStyles.padding}
        height="100%"
        gap={sizeStyles.labelIconGap}
        style={{
          opacity: loading ? 0 : 1,
          pointerEvents: loading ? "none" : "auto",
        }}
        aria-hidden={loading ? true : undefined}
      >
        {labelIcon && (
          <Box aria-hidden={true}>
            {cloneIconWithDefaults(
              labelIcon,
              sizeStyles.labelIconSize,
              textColor
            )}
          </Box>
        )}

        <Text color={textColor} fontSize={sizeStyles.fontSize} fontWeight="500">
          {children}
        </Text>

        {sublabel && (
          <Text
            color={textColor}
            fontSize={sizeStyles.fontSize}
            fontWeight="500"
            style={{ opacity: 0.4 }}
          >
            {sublabel}
          </Text>
        )}

        {customContent && <Box aria-hidden={true}>{customContent}</Box>}
      </Box>

      {iconRight && (
        <Box
          width={sizeStyles.iconContainerSize}
          height={sizeStyles.iconContainerSize}
          alignItems="center"
          justifyContent="center"
          aria-hidden={true}
          style={{
            opacity: loading ? 0 : 1,
            pointerEvents: loading ? "none" : "auto",
          }}
        >
          {cloneIconWithDefaults(iconRight, sizeStyles.iconSize, textColor)}
        </Box>
      )}
    </Box>
  );
};

AppButton.displayName = "AppButton";
